import { useState, useEffect } from 'react';
import { api } from '../services/api';
import toast from 'react-hot-toast';

// COMPONENTE PROVISIONAL - Modal para activar servicio por fallecimiento

interface Client {
  id: number;
  name: string;
  lastname: string;
  age: number;
  clientPlans: Array<{
    id: number;
    planTemplate: {
      name: string;
      maxFamilyMembers: number;
    };
    serviceActivations?: Array<any>;
  }>;
  familyMembers: Array<{
    id: number;
    name: string | null;
    lastname: string | null;
    relationship: string;
    age: number | null;
  }>;
}

interface ActivateServiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: Client | null;
  onSuccess: () => void;
}

interface Casket {
  id: number;
  name?: string;
  model: string;
  description?: string | null;
  material?: string;
  size?: string;
  price: number;
  stock?: number;
  isRental?: boolean;
  isAvailable: boolean;
}

type Step = 'DECEASED' | 'SERVICE' | 'REVIEW';

export const ActivateServiceModal = ({ 
  isOpen, 
  onClose, 
  client, 
  onSuccess 
}: ActivateServiceModalProps) => {
  const [step, setStep] = useState<Step>('DECEASED');
  const [deceasedType, setDeceasedType] = useState<'CLIENT' | 'FAMILY_MEMBER'>('FAMILY_MEMBER');
  const [selectedFamilyMemberId, setSelectedFamilyMemberId] = useState<number | null>(null);
  const [dateOfDeath, setDateOfDeath] = useState('');
  const [dateOfService, setDateOfService] = useState('');
  const [casketId, setCasketId] = useState<number | null>(null);
  const [serviceLocation, setServiceLocation] = useState('');
  const [chapelIncluded, setChapelIncluded] = useState(false);
  const [notes, setNotes] = useState('');
  const [availableCaskets, setAvailableCaskets] = useState<Casket[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchAvailableCaskets();
      // Resetear formulario
      setStep('DECEASED');
      setDeceasedType('FAMILY_MEMBER');
      setSelectedFamilyMemberId(null);
      setDateOfDeath('');
      setDateOfService('');
      setCasketId(null);
      setServiceLocation('');
      setChapelIncluded(false);
      setNotes('');
    }
  }, [isOpen]);

  const fetchAvailableCaskets = async () => {
    try {
      const response = await api.get('/caskets', {
        params: { isAvailable: true, stockStatus: 'in_stock' }
      });
      setAvailableCaskets(response.data.data || []);
    } catch (error) {
      console.error('Error fetching caskets:', error);
      toast.error('Error al cargar ataúdes disponibles');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!client) return;

    // Validaciones básicas
    if (deceasedType === 'FAMILY_MEMBER' && !selectedFamilyMemberId) {
      toast.error('Debe seleccionar un familiar');
      return;
    }

    if (!casketId) {
      toast.error('Debe seleccionar un ataúd');
      return;
    }

    if (!dateOfDeath) {
      toast.error('Debe ingresar la fecha de fallecimiento');
      return;
    }

    setLoading(true);

    try {
      const selectedFamilyMember = client.familyMembers.find(fm => fm.id === selectedFamilyMemberId);
      
      const data = {
        clientPlanId: client.clientPlans[0]?.id,
        deceasedType,
        deceasedClientId: deceasedType === 'CLIENT' ? client.id : null,
        deceasedFamilyId: deceasedType === 'FAMILY_MEMBER' ? selectedFamilyMemberId : null,
        deceasedName: deceasedType === 'CLIENT' 
          ? `${client.name} ${client.lastname}`
          : `${selectedFamilyMember?.name || ''} ${selectedFamilyMember?.lastname || ''}`.trim(),
        deceasedAge: deceasedType === 'CLIENT' ? client.age : selectedFamilyMember?.age,
        dateOfDeath,
        dateOfService: dateOfService || null,
        casketId,
        serviceLocation: serviceLocation || null,
        chapelIncluded,
        notes: notes || null
      };

      await api.post('/service-activations', data);
      
      toast.success('Servicio activado exitosamente');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error activating service:', error);
      toast.error(error.response?.data?.message || 'Error al activar el servicio');
    } finally {
      setLoading(false);
    }
  };

  if (!client) return null;

  const clientPlan = client.clientPlans[0];
  const servicesUsed = clientPlan?.serviceActivations?.length || 0;
  const maxServices = clientPlan?.planTemplate?.maxFamilyMembers || 8;
  const servicesAvailable = maxServices - servicesUsed;

  const canContinueFromDeceased =
    !!dateOfDeath &&
    (deceasedType === 'CLIENT' || !!selectedFamilyMemberId) &&
    servicesAvailable > 0;

  const canContinueFromService =
    !!casketId &&
    servicesAvailable > 0;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div 
        className="fixed inset-0 bg-black bg-opacity-50" 
        onClick={onClose}
      />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            {/* Encabezado */}
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-2xl font-semibold text-gray-900">
                  Activar servicio funerario
                </h2>
                <p className="mt-1 text-sm text-gray-600">
                  Registra el fallecimiento, define el servicio y asigna un ataúd disponible.
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            {/* Resumen del cliente y plan */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-100">
              <p className="text-sm text-gray-700">
                <strong>Cliente:</strong> {client.name} {client.lastname}
              </p>
              <p className="text-sm text-gray-700">
                <strong>Plan:</strong> {clientPlan?.planTemplate?.name}
              </p>
              <p className="text-sm text-gray-700">
                <strong>Servicios disponibles:</strong> {servicesAvailable} de {maxServices}
              </p>
            </div>

            {/* Alertas de capacidad */}
            {servicesAvailable <= 0 && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 text-sm">
                  El plan ha alcanzado el límite de servicios. No es posible activar nuevos servicios con este plan.
                </p>
              </div>
            )}

            {/* Stepper */}
            <div className="mb-6">
              <nav aria-label="Progreso" className="flex items-center justify-between text-xs text-gray-500">
                <div className="flex-1 flex items-center">
                  <div className={`flex h-7 w-7 items-center justify-center rounded-full border text-[11px] font-medium
                    ${step === 'DECEASED' ? 'border-gray-900 text-gray-900' : 'border-gray-300 text-gray-500'}`}>
                    1
                  </div>
                  <span className="ml-2">Fallecimiento</span>
                </div>
                <div className="flex-1 flex items-center justify-center">
                  <div className="h-px w-8 bg-gray-200" />
                </div>
                <div className="flex-1 flex items-center justify-center">
                  <div className={`flex h-7 w-7 items-center justify-center rounded-full border text-[11px] font-medium
                    ${step === 'SERVICE' ? 'border-gray-900 text-gray-900' : 'border-gray-300 text-gray-500'}`}>
                    2
                  </div>
                  <span className="ml-2">Servicio y ataúd</span>
                </div>
                <div className="flex-1 flex items-center justify-center">
                  <div className="h-px w-8 bg-gray-200" />
                </div>
                <div className="flex-1 flex items-center justify-end">
                  <div className={`flex h-7 w-7 items-center justify-center rounded-full border text-[11px] font-medium
                    ${step === 'REVIEW' ? 'border-gray-900 text-gray-900' : 'border-gray-300 text-gray-500'}`}>
                    3
                  </div>
                  <span className="ml-2">Revisión</span>
                </div>
              </nav>
            </div>

            {/* Formulario por pasos */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {step === 'DECEASED' && (
                <div className="space-y-5">
                  {/* Quién falleció */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      ¿Quién falleció? *
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center text-sm text-gray-800">
                        <input
                          type="radio"
                          value="CLIENT"
                          checked={deceasedType === 'CLIENT'}
                          onChange={(e) => setDeceasedType(e.target.value as 'CLIENT')}
                          className="mr-2"
                        />
                        <span>El titular: {client.name} {client.lastname} ({client.age} años)</span>
                      </label>
                      <label className="flex items-center text-sm text-gray-800">
                        <input
                          type="radio"
                          value="FAMILY_MEMBER"
                          checked={deceasedType === 'FAMILY_MEMBER'}
                          onChange={(e) => setDeceasedType(e.target.value as 'FAMILY_MEMBER')}
                          className="mr-2"
                        />
                        <span>Un familiar cubierto por el plan</span>
                      </label>
                    </div>
                  </div>

                  {/* Selección de familiar */}
                  {deceasedType === 'FAMILY_MEMBER' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Seleccionar familiar *
                      </label>
                      <select
                        value={selectedFamilyMemberId || ''}
                        onChange={(e) => setSelectedFamilyMemberId(Number(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900/10"
                      >
                        <option value="">Seleccione un familiar…</option>
                        {client.familyMembers.map((member) => (
                          <option key={member.id} value={member.id}>
                            {member.name} {member.lastname} - {member.relationship}
                            {member.age && ` (${member.age} años)`}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Fecha de fallecimiento */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fecha de fallecimiento *
                    </label>
                    <input
                      type="date"
                      value={dateOfDeath}
                      onChange={(e) => setDateOfDeath(e.target.value)}
                      max={new Date().toISOString().split('T')[0]}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900/10"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      La fecha no puede ser futura.
                    </p>
                  </div>
                </div>
              )}

              {step === 'SERVICE' && (
                <div className="space-y-5">
                  {/* Fecha del servicio */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fecha programada del servicio
                    </label>
                    <input
                      type="datetime-local"
                      value={dateOfService}
                      onChange={(e) => setDateOfService(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900/10"
                    />
                  </div>

                  {/* Selección de ataúd */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Seleccionar ataúd *
                    </label>
                    <select
                      value={casketId || ''}
                      onChange={(e) => setCasketId(Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900/10"
                    >
                      <option value="">Seleccione un ataúd…</option>
                      {availableCaskets.map((casket) => (
                        <option key={casket.id} value={casket.id}>
                          {(casket.name || casket.model)}
                          {casket.isRental ? ' (Renta)' : ''}
                          {' — '}stock {casket.stock ?? 0}
                          {' — '}${casket.price}
                        </option>
                      ))}
                    </select>
                    {availableCaskets.length === 0 && (
                      <p className="text-sm text-red-600 mt-1">
                        No hay ataúdes con stock disponible para asignar.
                      </p>
                    )}
                  </div>

                  {/* Ubicación del servicio */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ubicación del servicio
                    </label>
                    <input
                      type="text"
                      value={serviceLocation}
                      onChange={(e) => setServiceLocation(e.target.value)}
                      placeholder="Ej: Capilla central, sala 2"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900/10"
                    />
                  </div>

                  {/* Capilla incluida */}
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={chapelIncluded}
                        onChange={(e) => setChapelIncluded(e.target.checked)}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">
                        Incluir capilla ardiente
                      </span>
                    </label>
                  </div>

                  {/* Notas */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notas adicionales
                    </label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={3}
                      placeholder="Observaciones o detalles relevantes para la familia y el servicio…"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-gray-900/10"
                    />
                  </div>
                </div>
              )}

              {step === 'REVIEW' && (
                <div className="space-y-5 text-sm text-gray-800">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <h3 className="text-sm font-semibold text-gray-900 mb-2">
                        Fallecido
                      </h3>
                      <p>
                        <span className="text-gray-500">Tipo: </span>
                        {deceasedType === 'CLIENT' ? 'Titular del plan' : 'Familiar'}
                      </p>
                      <p>
                        <span className="text-gray-500">Nombre: </span>
                        {deceasedType === 'CLIENT'
                          ? `${client.name} ${client.lastname}`
                          : (() => {
                              const fm = client.familyMembers.find(f => f.id === selectedFamilyMemberId);
                              return fm ? `${fm.name} ${fm.lastname}` : '—';
                            })()}
                      </p>
                      <p>
                        <span className="text-gray-500">Fecha de fallecimiento: </span>
                        {dateOfDeath || '—'}
                      </p>
                    </div>

                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <h3 className="text-sm font-semibold text-gray-900 mb-2">
                        Servicio
                      </h3>
                      <p>
                        <span className="text-gray-500">Fecha del servicio: </span>
                        {dateOfService || 'Por definir'}
                      </p>
                      <p>
                        <span className="text-gray-500">Ubicación: </span>
                        {serviceLocation || 'Por definir'}
                      </p>
                      <p>
                        <span className="text-gray-500">Capilla ardiente: </span>
                        {chapelIncluded ? 'Incluida' : 'No incluida'}
                      </p>
                    </div>

                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <h3 className="text-sm font-semibold text-gray-900 mb-2">
                        Ataúd asignado
                      </h3>
                      {casketId ? (
                        (() => {
                          const casket = availableCaskets.find(c => c.id === casketId);
                          if (!casket) return <p>No se encontró información del ataúd seleccionado.</p>;
                          return (
                            <>
                              <p>
                                <span className="text-gray-500">Ataúd: </span>
                                {casket.name || casket.model}
                                {casket.isRental ? ' (Renta)' : ''}
                              </p>
                              <p>
                                <span className="text-gray-500">Stock restante: </span>
                                {casket.stock ?? 0}
                              </p>
                              <p>
                                <span className="text-gray-500">Descripción: </span>
                                {casket.description || '—'}
                              </p>
                              <p>
                                <span className="text-gray-500">Precio: </span>
                                ${casket.price.toLocaleString()}
                              </p>
                            </>
                          );
                        })()
                      ) : (
                        <p>No hay ataúd seleccionado.</p>
                      )}
                    </div>

                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <h3 className="text-sm font-semibold text-gray-900 mb-2">
                        Plan y capacidad
                      </h3>
                      <p>
                        <span className="text-gray-500">Plan: </span>
                        {clientPlan?.planTemplate?.name}
                      </p>
                      <p>
                        <span className="text-gray-500">Servicios usados: </span>
                        {servicesUsed} de {maxServices}
                      </p>
                      {notes && (
                        <p className="mt-2">
                          <span className="text-gray-500">Notas: </span>
                          {notes}
                        </p>
                      )}
                    </div>
                  </div>

                  <p className="text-xs text-gray-500">
                    Al confirmar se activará el servicio, se reservará el ataúd seleccionado y se descontará un servicio del plan.
                  </p>
                </div>
              )}

              {/* Controles de navegación */}
              <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                <div>
                  <button
                    type="button"
                    onClick={step === 'DECEASED' ? onClose : () => setStep(step === 'REVIEW' ? 'SERVICE' : 'DECEASED')}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
                    disabled={loading}
                  >
                    {step === 'DECEASED' ? 'Cancelar' : 'Atrás'}
                  </button>
                </div>
                <div className="flex items-center space-x-3">
                  {step !== 'REVIEW' && (
                    <button
                      type="button"
                      onClick={() => {
                        if (step === 'DECEASED' && canContinueFromDeceased) {
                          setStep('SERVICE');
                          return;
                        }
                        if (step === 'SERVICE' && canContinueFromService) {
                          setStep('REVIEW');
                        }
                      }}
                      className="px-4 py-2 bg-gray-900 text-white rounded-md text-sm hover:bg-gray-800 disabled:bg-gray-300"
                      disabled={
                        loading ||
                        servicesAvailable <= 0 ||
                        (step === 'DECEASED' && !canContinueFromDeceased) ||
                        (step === 'SERVICE' && !canContinueFromService)
                      }
                    >
                      Siguiente
                    </button>
                  )}
                  {step === 'REVIEW' && (
                    <button
                      type="submit"
                      className="px-4 py-2 bg-gray-900 text-white rounded-md text-sm hover:bg-gray-800 disabled:bg-gray-300"
                      disabled={loading || servicesAvailable <= 0 || availableCaskets.length === 0}
                    >
                      {loading ? 'Activando…' : 'Activar servicio'}
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

